import { type FormEvent, useEffect, useState } from 'react'
import { useLocale } from '../../shared/i18n/LocaleContext'
import { useAuth } from './AuthContext'
import { PasswordInput } from '../../shared/components/PasswordInput'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import { LanguageSelector } from '../../layout/LanguageSelector'
import { ThemeSelector } from '../../layout/ThemeSelector'
import { AppFooter } from '../../layout/AppFooter'

export function LoginPage() {
  const { t, locale, setLocale } = useLocale()
  const { login, sessionEndReason, acknowledgeSessionEnd } = useAuth()
  const { confirm, notice, confirmationDialog } = useAppConfirm()
  const [configuration, setConfiguration] = useState('')
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!sessionEndReason) return
    notice(t(sessionEndReason as Parameters<typeof t>[0]), t('auth.sessionEndedTitle'))
    acknowledgeSessionEnd()
  }, [acknowledgeSessionEnd, notice, sessionEndReason, t])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await login({ configuration, userName, password, rememberMe })
    } catch (reason) {
      if (reason instanceof Error && reason.message === 'auth.sessionAlreadyActive') {
        const replace = await confirm(
          t('auth.replaceSessionConfirm'),
          t('auth.sessionAlreadyActiveTitle'),
        )
        if (replace) {
          try {
            await login({ configuration, userName, password, rememberMe, replaceExistingSession: true })
          } catch (retryReason) {
            setError(retryReason instanceof Error ? retryReason.message : 'auth.invalidCredentials')
          }
        }
      } else setError(reason instanceof Error ? reason.message : 'auth.invalidCredentials')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="login-page">
      <section className="login-brand-panel">
        <div className="login-brand">
          <img
            className="brand-wordmark"
            src="/apl-enterprise-logo.png"
            alt="APL Enterprise"
          />
        </div>
        <div className="login-message">
          <span className="eyebrow">{t('auth.platform')}</span>
          <h1>{t('auth.heroTitle')}</h1>
          <p>{t('auth.heroSubtitle')}</p>
        </div>
        <small className="login-security">{t('auth.securityNote')}</small>
      </section>

      <section className="login-form-panel">
        <form className="login-card" onSubmit={submit}>
          <div className="login-language-selector">
            <ThemeSelector compact />
            <LanguageSelector
              locale={locale}
              label={t('user.language')}
              onChange={setLocale}
            />
          </div>
          <span className="eyebrow">{t('auth.welcome')}</span>
          <h2>{t('auth.signIn')}</h2>
          <p>{t('auth.signInSubtitle')}</p>

          <label>
            <span>{t('auth.configuration')}</span>
            <input
              value={configuration}
              onChange={(event) => setConfiguration(event.target.value)}
              autoComplete="organization"
              required
              autoFocus
            />
          </label>

          <label>
            <span>{t('auth.userName')}</span>
            <input
              value={userName}
              onChange={(event) => setUserName(event.target.value)}
              autoComplete="username"
              required
            />
          </label>

          <label>
            <span>{t('auth.password')}</span>
            <PasswordInput
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          <label className="remember-option">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
            />
            <span>{t('auth.rememberMe')}</span>
          </label>

          {error && (
            <div className="login-error" role="alert">
              {t(error as Parameters<typeof t>[0])}
            </div>
          )}

          <button className="login-button" disabled={isSubmitting} type="submit">
            {isSubmitting ? t('auth.signingIn') : t('auth.signInAction')}
          </button>
        </form>
      </section>
      <AppFooter standalone />
      {confirmationDialog}
    </main>
  )
}
